import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy, Sparkles, Target, Briefcase, MessageSquare, TrendingUp,
  Monitor, LayoutGrid, ChevronLeft, ChevronRight, Pause, Play,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "nova_hof_session";
const SLIDE_DURATION = 15_000; // 15 s per slide
const SLIDE_NAMES = ["Resumen Global", "Champion del Mes", "Trending Challenges", "De Reto a Portfolio", "Galería de Champions"];

// CSS keyframe injected once at module level
const HOF_STYLE = `
  @keyframes hof-progress {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
`;

// ─── slide variants ────────────────────────────────────────────────────────────
const slideVariants = {
  enter: (dir) => ({ opacity: 0, x: dir > 0 ? 80 : -80 }),
  center: { opacity: 1, x: 0 },
  exit:  (dir) => ({ opacity: 0, x: dir > 0 ? -80 : 80 }),
};

export default function NovaHallOfFamePage() {
  const [session, setSession] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");
  const [authing, setAuthing] = useState(false);
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const [tick, setTick] = useState(0);

  // Carousel state
  const [carouselMode, setCarouselMode] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideDir, setSlideDir] = useState(1);
  const [paused, setPaused] = useState(false);

  // ─── data refresh ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/nova/hof/data?session=${encodeURIComponent(session)}`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 401) { sessionStorage.removeItem(SESSION_KEY); setSession(""); return; }
        const d = await r.json();
        if (!cancelled) setData(d);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [session, tick]);

  useEffect(() => {
    if (!session) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [session]);

  // Live clock (every 30s)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  // ─── carousel navigation ──────────────────────────────────────────────────────
  const goToSlide = useCallback((newIdx, dir) => {
    setSlideDir(dir);
    setSlideIndex(newIdx);
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide((slideIndex + 1) % SLIDE_NAMES.length, 1);
  }, [slideIndex, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((slideIndex - 1 + SLIDE_NAMES.length) % SLIDE_NAMES.length, -1);
  }, [slideIndex, goToSlide]);

  // Auto-advance
  useEffect(() => {
    if (!carouselMode || paused) return undefined;
    const id = setTimeout(() => nextSlide(), SLIDE_DURATION);
    return () => clearTimeout(id);
  }, [carouselMode, paused, slideIndex, nextSlide]);

  // Keyboard navigation
  useEffect(() => {
    if (!carouselMode) return undefined;
    const handler = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      else if (e.key === "ArrowLeft") prevSlide();
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
      else if (e.key === "Escape") setCarouselMode(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [carouselMode, nextSlide, prevSlide]);

  // ─── Auth screen ──────────────────────────────────────────────────────────────
  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");
    setAuthing(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/hof/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pwd }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || "Contraseña incorrecta");
        return;
      }
      const d = await r.json();
      sessionStorage.setItem(SESSION_KEY, d.session);
      setSession(d.session);
      setPwd("");
    } finally {
      setAuthing(false);
    }
  };

  if (!session) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{
          background:
            "radial-gradient(circle at 15% 20%, rgba(59,173,45,0.25), transparent 45%), radial-gradient(circle at 85% 80%, rgba(251,191,36,0.18), transparent 50%), linear-gradient(135deg,#020c06 0%,#04140a 50%,#020c06 100%)",
        }}
      >
        <form onSubmit={handleAuth} className="w-full max-w-md rounded-3xl border border-emerald-500/20 bg-black/40 backdrop-blur-xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-2xl shadow-lg">🏆</div>
            <div>
              <div className="text-emerald-300 text-[10px] uppercase tracking-[0.3em] font-bold">NOVA · Innovation Radar</div>
              <h1 className="text-2xl font-black text-white">Hall of Fame</h1>
            </div>
          </div>
          <p className="text-emerald-100/70 text-sm mb-5">Pantalla pública para TV de oficina. Introduce la contraseña proporcionada por el equipo de innovación.</p>
          <Input
            type="password"
            placeholder="Contraseña"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            autoFocus
            data-testid="hof-password-input"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 rounded-xl"
          />
          {error && <div className="text-rose-300 text-sm mt-3" data-testid="hof-auth-error">{error}</div>}
          <Button type="submit" disabled={authing || !pwd} data-testid="hof-auth-submit"
            className="mt-5 w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold rounded-xl h-12">
            {authing ? "Entrando..." : "Entrar al Hall of Fame"}
          </Button>
        </form>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"/>
      </div>
    );
  }

  const cotm = data.champion_of_month?.champion;
  const history = data.history || [];
  const trending = data.trending || [];
  const celebrations = data.celebrations || [];
  const totals = data.totals || {};

  // ─── CAROUSEL MODE ────────────────────────────────────────────────────────────
  if (carouselMode) {
    return (
      <div
        className="min-h-screen h-screen w-screen overflow-hidden text-white flex flex-col"
        data-testid="hof-carousel"
        style={{
          background:
            "radial-gradient(circle at 15% 10%, rgba(59,173,45,0.22), transparent 45%), radial-gradient(circle at 90% 80%, rgba(251,191,36,0.15), transparent 50%), linear-gradient(135deg,#02080a 0%,#04140a 40%,#02080a 100%)",
        }}
      >
        <style>{HOF_STYLE}</style>
        {/* Carousel header */}
        <header className="flex items-center justify-between px-10 py-4 border-b border-emerald-500/10 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-xl shadow">🏆</div>
            <div>
              <div className="text-emerald-300 text-[9px] uppercase tracking-[0.3em] font-bold">NOVA · Innovation Radar</div>
              <h1 className="text-2xl font-black leading-tight">Hall of Fame · Valoriza</h1>
            </div>
          </div>
          {/* Slide name */}
          <div className="flex items-center gap-3">
            <AnimatePresence mode="wait">
              <motion.span
                key={slideIndex}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="text-sm font-semibold text-emerald-300/80 uppercase tracking-widest"
              >
                {SLIDE_NAMES[slideIndex]}
              </motion.span>
            </AnimatePresence>
            <span className="text-white/30 text-xs tabular-nums">{slideIndex + 1} / {SLIDE_NAMES.length}</span>
          </div>
          {/* Controls */}
          <div className="flex items-center gap-2">
            <div className="text-right mr-3">
              <div className="text-emerald-200/70 text-[10px] uppercase tracking-widest">
                {now.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" })}
              </div>
              <div className="text-lg font-bold tabular-nums">{now.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
            <button onClick={prevSlide} data-testid="carousel-prev"
              className="w-9 h-9 rounded-full border border-white/10 hover:border-emerald-500/50 flex items-center justify-center transition-colors">
              <ChevronLeft className="w-4 h-4"/>
            </button>
            <button onClick={() => setPaused((p) => !p)} data-testid="carousel-pause"
              className="w-9 h-9 rounded-full border border-white/10 hover:border-emerald-500/50 flex items-center justify-center transition-colors">
              {paused ? <Play className="w-4 h-4 text-emerald-400"/> : <Pause className="w-4 h-4"/>}
            </button>
            <button onClick={nextSlide} data-testid="carousel-next"
              className="w-9 h-9 rounded-full border border-white/10 hover:border-emerald-500/50 flex items-center justify-center transition-colors">
              <ChevronRight className="w-4 h-4"/>
            </button>
            <button onClick={() => setCarouselMode(false)} data-testid="carousel-exit-btn"
              title="Volver a vista grid (ESC)"
              className="ml-1 w-9 h-9 rounded-full border border-white/10 hover:border-amber-500/60 flex items-center justify-center transition-colors">
              <LayoutGrid className="w-4 h-4 text-amber-300"/>
            </button>
          </div>
        </header>

        {/* Slide content */}
        <div className="flex-1 relative overflow-hidden">
          <AnimatePresence custom={slideDir} mode="wait">
            <motion.div
              key={slideIndex}
              custom={slideDir}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 flex flex-col"
            >
              {slideIndex === 0 && <SlideStats totals={totals} />}
              {slideIndex === 1 && <SlideChampion cotm={cotm} />}
              {slideIndex === 2 && <SlideTrending trending={trending} />}
              {slideIndex === 3 && <SlideCelebrations celebrations={celebrations} />}
              {slideIndex === 4 && <SlideGallery history={history} />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress bar + dot indicators */}
        <div className="flex-shrink-0 px-10 py-4 flex flex-col gap-3">
          {/* Dots */}
          <div className="flex items-center justify-center gap-2">
            {SLIDE_NAMES.map((_, i) => (
              <button
                key={i}
                data-testid={`carousel-dot-${i}`}
                onClick={() => goToSlide(i, i > slideIndex ? 1 : -1)}
                className={`transition-all rounded-full ${
                  i === slideIndex
                    ? "w-8 h-2 bg-emerald-400"
                    : "w-2 h-2 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
          {/* Progress bar — CSS animation, no JS setState needed */}
          <div className="w-full h-[3px] rounded-full bg-white/10 overflow-hidden">
            <div
              key={`${slideIndex}-${paused ? "p" : "r"}`}
              className="h-full rounded-full origin-left"
              style={{
                background: paused ? "rgba(251,191,36,0.6)" : "linear-gradient(90deg,#3BAD2D,#34d399)",
                animation: paused ? "none" : `hof-progress ${SLIDE_DURATION}ms linear forwards`,
              }}
            />
          </div>
          <div className="text-center text-[10px] uppercase tracking-[0.25em] text-white/30">
            {paused ? "Pausado · Pulsa Espacio para reanudar" : "← → para navegar · Espacio para pausar · ESC para salir"}
          </div>
        </div>
      </div>
    );
  }

  // ─── GRID MODE (original) ─────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen text-white"
      data-testid="hof-screen"
      style={{
        background:
          "radial-gradient(circle at 15% 10%, rgba(59,173,45,0.22), transparent 45%), radial-gradient(circle at 90% 80%, rgba(251,191,36,0.15), transparent 50%), linear-gradient(135deg,#02080a 0%,#04140a 40%,#02080a 100%)",
      }}
    >
      {/* Header */}
      <header className="px-10 py-6 flex items-center justify-between border-b border-emerald-500/10">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-3xl shadow-lg">🏆</div>
          <div>
            <div className="text-emerald-300 text-[10px] uppercase tracking-[0.3em] font-bold">NOVA · Innovation Radar</div>
            <h1 className="text-4xl font-black tracking-tight">Hall of Fame · Valoriza</h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* TV Carousel Mode button */}
          <button
            onClick={() => { setCarouselMode(true); setSlideIndex(0); setPaused(false); }}
            data-testid="hof-carousel-toggle"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-sm font-semibold transition-all hover:border-emerald-400/60 hover:scale-105 active:scale-95"
            title="Modo TV: carrusel auto-rotativo para pantallas de oficina"
          >
            <Monitor className="w-4 h-4"/>
            <span>Modo TV</span>
          </button>
          <div className="text-right">
            <div className="text-emerald-200/80 text-xs uppercase tracking-widest">
              {now.toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long" })}
            </div>
            <div className="text-2xl font-bold tabular-nums">{now.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        </div>
      </header>

      {/* Top counters */}
      <section className="px-10 py-6 grid grid-cols-3 gap-6">
        <BigStat label="Retos NOVA" value={totals.challenges || 0} icon={<Target/>} color="#3BAD2D"/>
        <BigStat label="En portfolio" value={totals.in_portfolio || 0} icon={<Briefcase/>} color="#fbbf24"/>
        <BigStat label="Respuestas públicas" value={totals.submissions || 0} icon={<MessageSquare/>} color="#a78bfa"/>
      </section>

      <section className="px-10 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Champion of the month */}
        <div className="lg:col-span-5 rounded-3xl p-7 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg,#7c2d12 0%,#b45309 50%,#fbbf24 100%)" }}
          data-testid="hof-cotm">
          <div className="absolute right-3 top-3 text-[140px] leading-none opacity-15">🏆</div>
          <div className="relative">
            <div className="text-amber-100/90 text-[11px] uppercase tracking-[0.3em] font-bold">Champion of the Month</div>
            {cotm ? (
              <>
                <div className="text-xs text-amber-100/70 mt-2">{cotm.month_label}</div>
                <h2 className="text-5xl font-black mt-2 leading-tight">{cotm.name}</h2>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <MiniStat label="Score" value={cotm.score}/>
                  <MiniStat label="Retos" value={cotm.challenges}/>
                  <MiniStat label="En portfolio" value={cotm.in_portfolio}/>
                </div>
              </>
            ) : (
              <p className="text-amber-100/80 mt-4 text-lg">Aún no hay champion para el mes pasado. ¡Anímate a participar!</p>
            )}
          </div>
        </div>

        {/* Trending */}
        <div className="lg:col-span-7 rounded-3xl border border-emerald-500/20 bg-black/30 backdrop-blur p-6" data-testid="hof-trending">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-emerald-300"/>
            <h3 className="text-xl font-bold">Trending Challenges</h3>
            <span className="text-xs text-emerald-200/60 ml-auto">Top retos por votos</span>
          </div>
          {trending.length === 0 ? (
            <p className="text-emerald-100/60 text-sm">Sin retos en tendencia todavía.</p>
          ) : (
            <ol className="space-y-2">
              {trending.slice(0, 6).map((t, i) => (
                <motion.li key={t.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] p-3" data-testid={`hof-trending-${i}`}>
                  <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-300 font-black flex items-center justify-center">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{t.title}</div>
                    <div className="text-[11px] text-emerald-100/60 mt-0.5">{t.area || "—"} · {t.champion || "Sin champion"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-emerald-300 text-lg leading-none">{t.votes}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-100/50">votos</div>
                  </div>
                </motion.li>
              ))}
            </ol>
          )}
        </div>

        {/* Celebrations timeline */}
        <div className="lg:col-span-7 rounded-3xl border border-emerald-500/20 bg-black/30 backdrop-blur p-6" data-testid="hof-celebrations">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-emerald-300"/>
            <h3 className="text-xl font-bold">🎉 De reto a portfolio</h3>
            <span className="text-xs text-emerald-200/60 ml-auto">Celebraciones recientes</span>
          </div>
          {celebrations.length === 0 ? (
            <p className="text-emerald-100/60 text-sm">Aún no hay celebraciones.</p>
          ) : (
            <ol className="relative border-l-2 border-emerald-500/30 ml-2 space-y-4">
              <AnimatePresence>
                {celebrations.slice(0, 6).map((c, i) => (
                  <motion.li key={c.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} className="pl-5 relative">
                    <span className="absolute -left-[10px] top-1 w-4 h-4 rounded-full bg-emerald-500 ring-4 ring-emerald-500/20 flex items-center justify-center text-[10px]">🎉</span>
                    <div className="text-[10px] text-emerald-200/60 uppercase tracking-wider">
                      {new Date(c.celebrated_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                      {c.area && <> · {c.area}</>}
                    </div>
                    <div className="text-sm text-white/70 line-through decoration-emerald-400/40">«{(c.challenge_title || "").slice(0, 90)}»</div>
                    <div className="text-base font-bold text-emerald-300 mt-1">↳ {(c.initiative_name || "").slice(0, 90)}</div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ol>
          )}
        </div>

        {/* Champions gallery */}
        <div className="lg:col-span-5 rounded-3xl border border-amber-500/20 bg-black/30 backdrop-blur p-6" data-testid="hof-history">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-amber-300"/>
            <h3 className="text-xl font-bold">Galería de Champions</h3>
          </div>
          {history.length === 0 ? (
            <p className="text-amber-100/60 text-sm">¡El primer champion te está esperando!</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {history.slice(0, 6).map((h) => (
                <motion.div key={h.month_label} whileHover={{ y: -3 }}
                  className="rounded-2xl p-4 relative overflow-hidden text-white"
                  style={{ background: "linear-gradient(135deg,#7c2d12,#b45309 60%,#fbbf24)" }}
                  data-testid={`hof-history-${h.month_label.replace(/\s/g, '-')}`}>
                  <div className="absolute -right-2 -top-2 text-4xl opacity-20">🏆</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-100/90">{h.month_label}</div>
                  <div className="text-base font-black mt-1 leading-tight">{h.name}</div>
                  <div className="mt-2 text-[11px] text-amber-100/90">{h.challenges} retos · {h.in_portfolio} a portfolio</div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="px-10 py-4 border-t border-emerald-500/10 text-[10px] uppercase tracking-[0.3em] text-emerald-200/50 flex justify-between">
        <span>NOVA · Innovation OS Valoriza</span>
        <span>Actualizado automáticamente cada 60s</span>
      </footer>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Carousel Slides
// ──────────────────────────────────────────────────────────────────────────────

function SlideStats({ totals }) {
  const stats = [
    { label: "Retos NOVA", value: totals.challenges || 0, icon: "🎯", color: "#3BAD2D" },
    { label: "En Portfolio", value: totals.in_portfolio || 0, icon: "💼", color: "#fbbf24" },
    { label: "Respuestas Públicas", value: totals.submissions || 0, icon: "💬", color: "#a78bfa" },
  ];
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-16 gap-12" data-testid="slide-stats">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="text-center">
        <div className="text-emerald-300 text-xs uppercase tracking-[0.4em] font-bold mb-2">NOVA · Innovation Radar</div>
        <h2 className="text-6xl font-black tracking-tight">Impacto Global</h2>
      </motion.div>
      <div className="grid grid-cols-3 gap-8 w-full max-w-5xl">
        {stats.map((s, i) => (
          <motion.div key={s.label}
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 + i * 0.12, type: "spring", damping: 14 }}
            className="rounded-3xl border border-white/10 bg-black/30 backdrop-blur-xl p-10 flex flex-col items-center gap-4"
            data-testid={`slide-stat-${i}`}
          >
            <div className="text-6xl">{s.icon}</div>
            <div className="text-[5.5rem] font-black leading-none tabular-nums" style={{ color: s.color }}>
              {s.value}
            </div>
            <div className="text-base uppercase tracking-[0.25em] text-white/60 font-semibold">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function SlideChampion({ cotm }) {
  return (
    <div className="flex-1 flex items-center justify-center px-20" data-testid="slide-champion">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-3xl rounded-[2.5rem] p-14 relative overflow-hidden text-white shadow-2xl"
        style={{ background: "linear-gradient(135deg,#7c2d12 0%,#b45309 45%,#fbbf24 100%)" }}
      >
        <div className="absolute right-6 top-6 text-[240px] leading-none opacity-10 select-none">🏆</div>
        <div className="relative">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
            className="text-amber-100/90 text-sm uppercase tracking-[0.4em] font-bold">
            Champion of the Month
          </motion.div>
          {cotm ? (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                className="text-sm text-amber-100/70 mt-3">{cotm.month_label}</motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, type: "spring", damping: 12 }}
                className="text-8xl font-black mt-4 leading-tight tracking-tight">{cotm.name}
              </motion.h2>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
                className="mt-10 grid grid-cols-3 gap-4">
                <MiniStat label="Score" value={cotm.score}/>
                <MiniStat label="Retos" value={cotm.challenges}/>
                <MiniStat label="En portfolio" value={cotm.in_portfolio}/>
              </motion.div>
            </>
          ) : (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              className="text-amber-100/80 mt-6 text-3xl font-light leading-relaxed">
              Aún no hay champion para el mes pasado.<br/>
              <span className="font-black">¡Anímate a participar!</span>
            </motion.p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function SlideTrending({ trending }) {
  return (
    <div className="flex-1 flex flex-col px-16 py-8 gap-6" data-testid="slide-trending">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
        <TrendingUp className="w-8 h-8 text-emerald-400"/>
        <h2 className="text-5xl font-black">Trending Challenges</h2>
        <span className="ml-auto text-emerald-200/60 text-sm uppercase tracking-widest">Top por votos</span>
      </motion.div>
      {trending.length === 0 ? (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-emerald-100/60 text-xl mt-10">Sin retos en tendencia todavía.</motion.p>
      ) : (
        <ol className="flex-1 grid grid-cols-1 gap-3 content-start">
          {trending.slice(0, 8).map((t, i) => (
            <motion.li key={t.id}
              initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.07, type: "spring", damping: 16 }}
              className="flex items-center gap-5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.08] px-6 py-4"
              data-testid={`slide-trending-${i}`}>
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-300 font-black text-xl flex items-center justify-center flex-shrink-0">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-lg font-bold truncate">{t.title}</div>
                <div className="text-sm text-emerald-100/60 mt-0.5">{t.area || "—"} · {t.champion || "Sin champion"}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-black text-emerald-300 text-3xl leading-none tabular-nums">{t.votes}</div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-100/50">votos</div>
              </div>
            </motion.li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SlideCelebrations({ celebrations }) {
  return (
    <div className="flex-1 flex flex-col px-16 py-8 gap-6" data-testid="slide-celebrations">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
        <Sparkles className="w-8 h-8 text-emerald-400"/>
        <h2 className="text-5xl font-black">🎉 De Reto a Portfolio</h2>
        <span className="ml-auto text-emerald-200/60 text-sm uppercase tracking-widest">Últimas celebraciones</span>
      </motion.div>
      {celebrations.length === 0 ? (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-emerald-100/60 text-xl mt-10">
          Aún no hay celebraciones. ¡Convierte un reto en iniciativa!
        </motion.p>
      ) : (
        <ol className="relative border-l-2 border-emerald-500/30 ml-4 space-y-6 flex-1 content-start">
          {celebrations.slice(0, 7).map((c, i) => (
            <motion.li key={c.id}
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08, type: "spring", damping: 16 }}
              className="pl-8 relative" data-testid={`slide-celebration-${i}`}>
              <span className="absolute -left-[14px] top-2 w-6 h-6 rounded-full bg-emerald-500 ring-4 ring-emerald-500/20 flex items-center justify-center text-sm">🎉</span>
              <div className="text-xs text-emerald-200/60 uppercase tracking-wider mb-0.5">
                {new Date(c.celebrated_at).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}
                {c.area && <span className="ml-2">· {c.area}</span>}
              </div>
              <div className="text-base text-white/60 line-through decoration-emerald-400/40">«{(c.challenge_title || "").slice(0, 100)}»</div>
              <div className="text-xl font-black text-emerald-300 mt-1">↳ {(c.initiative_name || "").slice(0, 100)}</div>
            </motion.li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SlideGallery({ history }) {
  return (
    <div className="flex-1 flex flex-col px-16 py-8 gap-6" data-testid="slide-gallery">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
        <Trophy className="w-8 h-8 text-amber-400"/>
        <h2 className="text-5xl font-black">Galería de Champions</h2>
        <span className="ml-auto text-amber-200/60 text-sm uppercase tracking-widest">Hall of Fame</span>
      </motion.div>
      {history.length === 0 ? (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-amber-100/60 text-xl mt-10">
          ¡El primer champion del Hall of Fame te está esperando!
        </motion.p>
      ) : (
        <div className="flex-1 grid grid-cols-3 gap-5 content-start">
          {history.slice(0, 9).map((h, i) => (
            <motion.div key={h.month_label}
              initial={{ opacity: 0, scale: 0.88, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: i * 0.07, type: "spring", damping: 14 }}
              className="rounded-2xl p-6 relative overflow-hidden text-white"
              style={{ background: "linear-gradient(135deg,#7c2d12,#b45309 60%,#fbbf24)" }}
              data-testid={`slide-gallery-${i}`}>
              <div className="absolute -right-3 -top-3 text-6xl opacity-15 select-none">🏆</div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-100/90">{h.month_label}</div>
              <div className="text-2xl font-black mt-2 leading-tight">{h.name}</div>
              <div className="mt-3 text-sm text-amber-100/80">{h.challenges} retos · {h.in_portfolio} a portfolio</div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Reusable atoms
// ──────────────────────────────────────────────────────────────────────────────

function BigStat({ label, value, icon, color }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-black/30 backdrop-blur p-6 flex items-center gap-5">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${color}22`, color }}>
        <span className="w-6 h-6">{icon}</span>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60">{label}</div>
        <div className="text-5xl font-black tabular-nums mt-1" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-black/20 p-3 backdrop-blur">
      <div className="text-[10px] uppercase tracking-widest text-amber-100/80 font-bold">{label}</div>
      <div className="text-2xl font-black mt-0.5">{value}</div>
    </div>
  );
}
